import React, { useState, useEffect, useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import { FileText, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import { InfoHint } from './InfoHint';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { RichTextField } from './RichTextField';
import { ProjectTagsField } from './ProjectTagsField';
import { EntityTagsField, mentionableToEntityTagOption } from './EntityTagsField';
import { DatePicker } from './DatePicker';
import { TierLimitModal } from './TierLimitModal';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { dispatchMentionableEntitiesChanged } from '../utils/mentionable-entities-events';
import { extractMentionedEntityIds, buildHiddenMentionsHtml } from '../utils/entity-mentions';
import type { MentionableEntity } from './RichTextField';

interface AddGenericResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  initialTitle?: string;
  onCreated?: (resource: { id: string; title: string }) => void;
}

export const AddGenericResourceModal: React.FC<AddGenericResourceModalProps> = ({
  isOpen,
  onClose,
  projectId,
  initialTitle = '',
  onCreated,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const [title, setTitle] = useState('');
  const [resourceDate, setResourceDate] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResourceLimitModal, setShowResourceLimitModal] = useState(false);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);

  useEffect(() => {
    if (!isOpen || !projectId || !token) {
      return;
    }
    const fetchMentionableEntities = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };
    void fetchMentionableEntities();
  }, [isOpen, projectId, token]);

  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setResourceDate('');
      setContent('');
      setTags([]);
      setSelectedCharacterIds([]);
      setSelectedLocationIds([]);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, initialTitle]);

  const characterOptions = useMemo(
    () => mentionableEntities.filter((entity) => entity.type === 'character').map(mentionableToEntityTagOption),
    [mentionableEntities]
  );
  const locationOptions = useMemo(
    () => mentionableEntities.filter((entity) => entity.type === 'location').map(mentionableToEntityTagOption),
    [mentionableEntities]
  );
  const characterOptionsById = useMemo(() => new Map(characterOptions.map((o) => [o.id, o])), [characterOptions]);
  const locationOptionsById = useMemo(() => new Map(locationOptions.map((o) => [o.id, o])), [locationOptions]);

  const handleContentChange = (html: string): void => {
    setContent(html);
    const mentionedCharacterIds = extractMentionedEntityIds(html, 'character');
    const mentionedLocationIds = extractMentionedEntityIds(html, 'location');
    if (mentionedCharacterIds.length > 0) {
      setSelectedCharacterIds((prev) => Array.from(new Set([...prev, ...mentionedCharacterIds])));
    }
    if (mentionedLocationIds.length > 0) {
      setSelectedLocationIds((prev) => Array.from(new Set([...prev, ...mentionedLocationIds])));
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!title.trim()) {
      setError(t('title_required'));
      return;
    }

    if (!token) {
      setError(t('not_authenticated'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const contentCharacterMentionIds = new Set(extractMentionedEntityIds(content, 'character'));
      const contentLocationMentionIds = new Set(extractMentionedEntityIds(content, 'location'));
      const characterMentions = buildHiddenMentionsHtml(
        'character',
        selectedCharacterIds.filter((id) => !contentCharacterMentionIds.has(id)),
        characterOptionsById
      );
      const locationMentions = buildHiddenMentionsHtml(
        'location',
        selectedLocationIds.filter((id) => !contentLocationMentionIds.has(id)),
        locationOptionsById
      );
      const hiddenMentions = [characterMentions, locationMentions].filter(Boolean).join(' ');
      const contentWithLinks = hiddenMentions
        ? `${content.trim()}<p style="display:none" data-resource-linked-entities="true">${hiddenMentions}</p>`
        : content.trim();

      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}/resources/generic`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: title.trim(),
            resourceDate: /^\d{4}-\d{2}-\d{2}$/.test(resourceDate.trim()) ? resourceDate.trim() : undefined,
            content: contentWithLinks || undefined,
            tags: tags.length > 0 ? tags : undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'RESOURCE_LIMIT_REACHED') {
          setShowResourceLimitModal(true);
          setIsSubmitting(false);
          return;
        }
        throw new Error(errorData.error || t('failed_to_add_generic_resource'));
      }

      const data = await response.json();
      window.dispatchEvent(
        new CustomEvent('resource-uploaded', {
          detail: { resourceIds: [data.id], projectId },
        })
      );
      dispatchMentionableEntitiesChanged(projectId);
      onCreated?.({ id: data.id, title: data.title ?? title.trim() });
      onClose();
    } catch (err) {
      console.error('Error adding generic resource:', err);
      setError(err instanceof Error ? err.message : t('failed_to_add_generic_resource'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
        <DialogContent
          className={`${ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME} sm:max-w-3xl`}
          data-create-entity-from-mention=""
        >
          <DialogTitle className="sr-only">{t('add_generic_resource')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('add_generic_resource_description')}
          </DialogDescription>
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <EntityDetailModalLayout
              header={<EntityDetailModalTitle>{t('add_generic_resource')}</EntityDetailModalTitle>}
              footer={
                <DialogFooter className="gap-2 sm:justify-end">
                  <CancelButton
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting || !title.trim()}
                    dialogPrimary
                    className="inline-flex items-center gap-1.5"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('adding')}
                      </>
                    ) : (
                      <>
                        {t('add')}
                        <DialogPrimaryShortcut className="ml-0.5" />
                      </>
                    )}
                  </Button>
                </DialogFooter>
              }
            >
              <div className="grid grid-cols-1 gap-6 p-4 md:grid-cols-[3fr_2fr]">
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">{t('Name')}</label>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={t('add_generic_resource_title_placeholder')}
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          setError(null);
                        }}
                        disabled={isSubmitting}
                        autoFocus
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col">
                    <label className="mb-1.5 block text-sm font-medium">{t('content_label')}</label>
                    <RichTextField
                      value={content}
                      onChange={handleContentChange}
                      placeholder={t('add_generic_resource_content_placeholder')}
                      minHeight="280px"
                      mentionableEntities={mentionableEntities}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-4 border-t pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
                  <div>
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <label className="text-sm font-medium">{t('reference_date_label')}</label>
                      <InfoHint label={t('reference_date_label')} iconClassName="h-3.5 w-3.5">
                        {t('reference_date_tooltip')}
                      </InfoHint>
                    </div>
                    <DatePicker
                      value={resourceDate}
                      onChange={setResourceDate}
                      disabled={isSubmitting}
                      allowEmpty
                    />
                  </div>

                  {projectId && (
                    <ProjectTagsField
                      projectId={projectId}
                      tags={tags}
                      onTagsChange={setTags}
                      disabled={isSubmitting}
                      contentClassName="z-[2100]"
                      compact
                    />
                  )}

                  <EntityTagsField
                    label={t('filter_characters')}
                    entityType="character"
                    options={characterOptions}
                    selectedIds={selectedCharacterIds}
                    onChange={setSelectedCharacterIds}
                    disabled={isSubmitting}
                    emptyHint={t('no_characters_yet_hint')}
                  />

                  <EntityTagsField
                    label={t('filter_locations')}
                    entityType="location"
                    options={locationOptions}
                    selectedIds={selectedLocationIds}
                    onChange={setSelectedLocationIds}
                    disabled={isSubmitting}
                    emptyHint={t('no_locations_yet_hint')}
                  />
                </div>

                {error && (
                  <p className="text-sm text-destructive md:col-span-2">{error}</p>
                )}
              </div>
            </EntityDetailModalLayout>
          </form>
        </DialogContent>
      </Dialog>
      <TierLimitModal
        open={showResourceLimitModal}
        onOpenChange={setShowResourceLimitModal}
        limitType="resource"
      />
    </>
  );
};
